import PropTypes from 'prop-types';
import { createContext, useState } from 'react';

// project import
import defaultConfig from 'config';

// project hooks
import useLocalStorage from 'hooks/useLocalStorage';
import {
    DEFAULT_TENANT_PRESET_COLOR,
    getTenantBrandingFromPublicResponse,
    getTenantBrandingFromSettings,
    normaliseTenantPresetColor
} from './tenantAppearance';

// initial state
const initialState = {
    ...defaultConfig,
    onChangeMenuType: () => {},
    onChangeLocale: () => {},
    onChangeRTL: () => {},
    onChangeContainer: () => {},
    onChangeFontFamily: () => {},
    onChangeBorderRadius: () => {},
    onChangeOutlinedField: () => {},
    onChangeDNDMode: () => {},
    setConfigByName: () => {}
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState);

const ConfigProvider = ({ children }) => {
    const [config, setConfig] = useLocalStorage('gestop-config', {
        fontFamily: initialState.fontFamily,
        borderRadius: initialState.borderRadius,
        outlinedFilled: initialState.outlinedFilled,
        navType: initialState.navType,
        locale: initialState.locale,
        rtlLayout: initialState.rtlLayout,
        dndMode: initialState.dndMode,
        notificationChannels: initialState.notificationChannels,
        container: initialState.container
    });
    const [tenantBranding, setTenantBranding] = useState({
        presetColor: DEFAULT_TENANT_PRESET_COLOR,
        enterpriseLogo: null,
        enterpriseLogoDark: null,
        loginWallpaper: null
    });
    const persistedConfig = { ...(config || {}) };
    delete persistedConfig.presetColor;

    const savePersistentConfig = (updates = {}) => {
        const nextPersistentConfig = { ...persistedConfig, ...updates };
        delete nextPersistentConfig.presetColor;
        setConfig(nextPersistentConfig);
    };

    const resolvedConfig = {
        fontFamily: initialState.fontFamily,
        borderRadius: initialState.borderRadius,
        outlinedFilled: initialState.outlinedFilled,
        navType: initialState.navType,
        locale: initialState.locale,
        rtlLayout: initialState.rtlLayout,
        dndMode: initialState.dndMode,
        notificationChannels: initialState.notificationChannels,
        container: initialState.container,
        ...persistedConfig,
        presetColor: tenantBranding.presetColor,
        enterpriseLogo: tenantBranding.enterpriseLogo,
        enterpriseLogoDark: tenantBranding.enterpriseLogoDark,
        loginWallpaper: tenantBranding.loginWallpaper
    };

    const onChangeMenuType = (navType) => {
        savePersistentConfig({ navType });
    };

    const onChangeLocale = (locale) => {
        savePersistentConfig({ locale });
    };

    const onChangeRTL = (rtlLayout) => {
        savePersistentConfig({ rtlLayout });
    };

    const onChangeContainer = () => {
        savePersistentConfig({ container: !resolvedConfig.container });
    };

    const onChangeFontFamily = (fontFamily) => {
        savePersistentConfig({ fontFamily });
    };

    const onChangeBorderRadius = (event, newValue) => {
        savePersistentConfig({ borderRadius: newValue });
    };

    const onChangeOutlinedField = (outlinedFilled) => {
        savePersistentConfig({ outlinedFilled });
    };

    const onChangeDNDMode = (dndMode) => {
        savePersistentConfig({ dndMode });
    };

    const setConfigByName = (configName, configValue) => {
        savePersistentConfig({ [configName]: configValue });
    };

    const updateConfig = (updConfig) => {
        savePersistentConfig(updConfig);
    };

    const applyTenantPresetColor = (presetColor) => {
        setTenantBranding((current) => ({ ...current, presetColor: normaliseTenantPresetColor(presetColor) }));
    };

    const resetTenantPresetColor = () => {
        setTenantBranding({
            presetColor: DEFAULT_TENANT_PRESET_COLOR,
            enterpriseLogo: null,
            enterpriseLogoDark: null,
            loginWallpaper: null
        });
    };

    const applyTenantBranding = (settings) => {
        setTenantBranding(getTenantBrandingFromSettings(settings));
    };

    const applyPublicBranding = (branding) => {
        setTenantBranding(getTenantBrandingFromPublicResponse(branding));
    };

    return (
        <ConfigContext.Provider
            value={{
                ...resolvedConfig,
                onChangeMenuType,
                onChangeLocale,
                onChangeRTL,
                onChangeContainer,
                onChangeFontFamily,
                onChangeBorderRadius,
                onChangeOutlinedField,
                onChangeDNDMode,
                setConfigByName,
                updateConfig,
                applyTenantPresetColor,
                applyTenantBranding,
                applyPublicBranding,
                resetTenantPresetColor
            }}
        >
            {children}
        </ConfigContext.Provider>
    );
};

ConfigProvider.propTypes = {
    children: PropTypes.node
};

export { ConfigProvider, ConfigContext };
